import { lazy, type ComponentType } from "react";
import {
  isChunkLoadError,
  markChunkLoadSuccess,
  reloadOnceForChunkFailure,
} from "@/lib/chunk-reload";

/**
 * React.lazy with stale-chunk self-heal. See @/lib/chunk-reload for why the
 * reload is one-shot per session — the loop guard is load-bearing.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // After a deploy, a stale modulepreload can make import() resolve to an
      // empty/undefined module instead of rejecting (Cloudflare Pages serves
      // index.html for the missing hashed chunk). React.lazy then throws
      // "undefined is not an object (evaluating '_result.default')". Treat a
      // missing default the same as a fetch failure.
      if (!mod || typeof mod.default === "undefined") {
        // Reload once for fresh chunks. If the guard is already spent
        // (repeated failures in one session), surface a chunk-load-shaped
        // error so isChunkLoadError + Sentry's ignoreErrors handle it,
        // instead of falling through and letting React.lazy throw a cryptic
        // "_result.default" TypeError on every retry.
        if (reloadOnceForChunkFailure()) {
          return new Promise<{ default: T }>(() => {});
        }
        throw new Error("error loading dynamically imported module");
      }
      // A chunk that actually loaded means the session is on a coherent
      // build again, so re-arm the one-shot reload guard for the next deploy.
      markChunkLoadSuccess();
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && reloadOnceForChunkFailure()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
